import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CalendarCheck, Globe, Check, Briefcase, HeartHandshake } from "lucide-react";
import { Reveal, MaskedLine } from "@/components/Motion";
import BookButton from "@/components/BookButton";
import { useLang } from "@/i18n";
import { SEO } from "@/components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Field = ({ label, optional, children, testid, optionalLabel }) => (
  <div data-testid={testid}>
    <label className="mb-2 block text-sm font-medium text-obsidian">
      {label} {optional && <span className="font-normal text-slate-400">{optionalLabel}</span>}
    </label>
    {children}
  </div>
);

const ConsultationCard = () => {
  const { t } = useLang();
  return (
    <Reveal className="h-full">
      <div
        data-testid="consultation-card"
        className="relative flex h-full flex-col overflow-hidden rounded-2xl bg-obsidian p-8 sm:p-10"
      >
        <div className="pointer-events-none absolute -top-16 end-0 h-56 w-56 rounded-full bg-gold/10 blur-[80px]" />
        <div className="relative">
          <span className="flex h-12 w-12 items-center justify-center rounded-xl border border-gold/30 bg-obsidian-card text-gold">
            <CalendarCheck className="h-5 w-5" />
          </span>
          <h2 className="mt-6 font-serif text-3xl text-white">{t("contact.consultTitle")}</h2>
          <p className="mt-4 text-sm leading-relaxed text-slate-400">{t("contact.consultText")}</p>
          <ul className="mt-8 space-y-3.5">
            {t("contact.consultPoints").map((item, i) => (
              <li key={i} className="flex items-start gap-3 text-sm text-slate-300">
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
                {item}
              </li>
            ))}
          </ul>
          <div className="mt-10">
            <BookButton testid="contact-book-consultation-button" fallback="#inquiry-form" className="btn-gold w-full">
              {t("contact.consultBtn")}
            </BookButton>
          </div>
          <div className="mt-10 flex items-start gap-3 border-t border-white/10 pt-8">
            <Globe className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
            <p className="text-xs leading-relaxed text-slate-400">{t("contact.consultNote")}</p>
          </div>
        </div>
      </div>
    </Reveal>
  );
};

export default function Contact() {
  const { t, c, lang } = useLang();
  const [searchParams] = useSearchParams();
  const plan = c.plans.find((p) => p.slug === searchParams.get("plan"));
  const supportSvc = c.services.find((s) => s.slug === searchParams.get("support"));
  const intent = searchParams.get("intent");

  const SUPPORT_OPTIONS = [
    ...c.services.map((s) => s.title),
    t("contact.freeConsultation"),
    t("contact.customProject"),
    t("contact.notSure"),
  ];

  const initial = {
    name: "",
    company: "",
    email: "",
    phone: "",
    country: "",
    support_type: intent === "consultation" ? t("contact.freeConsultation") : supportSvc ? supportSvc.title : "",
    hours_per_week: "",
    preferred_contact: "",
    description:
      intent === "referral"
        ? t("contact.referralPrefill")
        : plan
          ? t("contact.planPrefill").replace("{name}", plan.name)
          : "",
  };

  const [form, setForm] = useState(initial);
  const [sending, setSending] = useState(false);
  const [track, setTrack] = useState(() => {
    const tr = searchParams.get("track");
    return tr === "personal" || tr === "business" ? tr : "";
  });

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!track) {
      toast.error(t("contact.trackRequired"));
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/inquiries`, { ...form, track, lang });
      toast.success(t("contact.successTitle"), { description: t("contact.successText") });
      setForm({ ...initial, support_type: "", description: "" });
      setTrack("");
    } catch (err) {
      toast.error(t("contact.errorTitle"), { description: t("contact.errorText") });
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <SEO
        title="Contact Us | VCK Business Lab"
        description="Tell us about your business and get a complimentary consultation. Virtual support worldwide — in English, Ukrainian, Arabic, and other languages."
        path="/contact"
      />
      <section data-testid="contact-header" className="bg-alabaster pb-16 pt-40">
        <div className="mx-auto max-w-7xl px-6 sm:px-12 lg:px-20">
          <MaskedLine delay={0.1}>
            <span className="overline text-gold-muted">{t("contact.overline")}</span>
          </MaskedLine>
          <h1 className="mt-6 max-w-3xl font-serif text-4xl font-light leading-[1.1] tracking-tight text-obsidian sm:text-5xl lg:text-6xl">
            <MaskedLine delay={0.25}>{t("contact.line1")}</MaskedLine>
            <MaskedLine delay={0.4}>
              <span className="italic text-gold-muted">{t("contact.line2")}</span>
            </MaskedLine>
          </h1>
          <MaskedLine delay={0.6}>
            <span className="mt-8 block max-w-2xl text-base leading-relaxed text-slate-600 sm:text-lg">
              {t("contact.intro")}
            </span>
          </MaskedLine>
        </div>
      </section>

      <section className="bg-alabaster pb-24 lg:pb-32">
        <div className="mx-auto grid max-w-7xl gap-10 px-6 sm:px-12 lg:grid-cols-5 lg:px-20">
          <div className="lg:col-span-2">
            <ConsultationCard />
          </div>
          <Reveal delay={0.1} className="lg:col-span-3">
            <form
              id="inquiry-form"
              data-testid="contact-form"
              onSubmit={onSubmit}
              className="rounded-2xl border border-obsidian/10 bg-white p-8 shadow-sm sm:p-10"
            >
              <h2 className="font-serif text-2xl text-obsidian">{t("contact.formTitle")}</h2>
              <p className="mt-2 text-sm text-slate-500">{t("contact.formSub")}</p>
              <div className="mt-8" data-testid="contact-field-track">
                <label className="mb-2 block text-sm font-medium text-obsidian">{t("contact.trackLabel")}</label>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {[
                    { value: "business", label: t("contact.trackBusiness"), icon: Briefcase },
                    { value: "personal", label: t("contact.trackPersonal"), icon: HeartHandshake },
                  ].map(({ value, label, icon: Icon }) => (
                    <button
                      key={value}
                      type="button"
                      data-testid={`contact-track-${value}`}
                      onClick={() => setTrack(value)}
                      aria-pressed={track === value}
                      className={`flex items-center gap-3 rounded-xl border px-4 py-3.5 text-sm font-medium transition-all duration-300 ${
                        track === value
                          ? "border-gold bg-gold/10 text-obsidian shadow-sm"
                          : "border-obsidian/10 bg-white text-slate-500 hover:border-gold/50 hover:text-obsidian"
                      }`}
                    >
                      <Icon className={`h-4 w-4 shrink-0 ${track === value ? "text-gold-muted" : "text-slate-400"}`} />
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="mt-6 grid gap-6 sm:grid-cols-2">
                <Field label={t("contact.name")} testid="contact-field-name">
                  <input
                    data-testid="contact-name-input"
                    name="name"
                    required
                    value={form.name}
                    onChange={onChange}
                    className="input-field"
                    placeholder={t("contact.phName")}
                  />
                </Field>
                <Field label={t("contact.company")} optional optionalLabel={t("contact.optional")} testid="contact-field-company">
                  <input
                    data-testid="contact-company-input"
                    name="company"
                    value={form.company}
                    onChange={onChange}
                    className="input-field"
                    placeholder={t("contact.phCompany")}
                  />
                </Field>
                <Field label={t("contact.email")} testid="contact-field-email">
                  <input
                    data-testid="contact-email-input"
                    type="email"
                    name="email"
                    required
                    value={form.email}
                    onChange={onChange}
                    className="input-field"
                    placeholder={t("contact.phEmail")}
                  />
                </Field>
                <Field label={t("contact.phone")} optional optionalLabel={t("contact.optional")} testid="contact-field-phone">
                  <input
                    data-testid="contact-phone-input"
                    name="phone"
                    value={form.phone}
                    onChange={onChange}
                    className="input-field"
                    placeholder={t("contact.phPhone")}
                  />
                </Field>
                <Field label={t("contact.country")} testid="contact-field-country">
                  <input
                    data-testid="contact-country-input"
                    name="country"
                    required
                    value={form.country}
                    onChange={onChange}
                    className="input-field"
                    placeholder={t("contact.phCountry")}
                  />
                </Field>
                <Field label={t("contact.support")} testid="contact-field-support">
                  <select
                    data-testid="contact-support-select"
                    name="support_type"
                    required
                    value={form.support_type}
                    onChange={onChange}
                    className="input-field"
                  >
                    <option value="" disabled>
                      {t("contact.selectService")}
                    </option>
                    {SUPPORT_OPTIONS.map((o) => (
                      <option key={o} value={o}>
                        {o}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label={t("contact.hours")} testid="contact-field-hours">
                  <select
                    data-testid="contact-hours-select"
                    name="hours_per_week"
                    required
                    value={form.hours_per_week}
                    onChange={onChange}
                    className="input-field"
                  >
                    <option value="" disabled>
                      {t("contact.selectHours")}
                    </option>
                    {t("contact.hoursOptions").map((o) => (
                      <option key={o} value={o}>
                        {o}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label={t("contact.preferred")} testid="contact-field-preferred">
                  <select
                    data-testid="contact-preferred-select"
                    name="preferred_contact"
                    required
                    value={form.preferred_contact}
                    onChange={onChange}
                    className="input-field"
                  >
                    <option value="" disabled>
                      {t("contact.selectPreferred")}
                    </option>
                    {t("contact.contactOptions").map((o) => (
                      <option key={o} value={o}>
                        {o}
                      </option>
                    ))}
                  </select>
                </Field>
                <div className="sm:col-span-2" data-testid="contact-field-description">
                  <label className="mb-2 block text-sm font-medium text-obsidian">{t("contact.description")}</label>
                  <textarea
                    data-testid="contact-description-input"
                    name="description"
                    required
                    rows={5}
                    value={form.description}
                    onChange={onChange}
                    className="input-field resize-none"
                    placeholder={t("contact.phDescription")}
                  />
                </div>
              </div>
              <button
                data-testid="contact-submit-button"
                type="submit"
                disabled={sending}
                className="btn-gold mt-8 w-full disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
              >
                {sending ? t("contact.sending") : t("contact.submit")}
              </button>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
